import React, { useState, useMemo, useEffect } from 'react';
import { motion } from 'motion/react';
import { 
  Shield, 
  Activity, 
  FileText, 
  Search, 
  Lock, 
  UserCheck, 
  RefreshCw, 
  AlertTriangle, 
  Globe, 
  Server, 
  HardDrive, 
  Download,
  AlertCircle,
  Eye,
  CheckCircle,
  Clock,
  Terminal,
  Database
} from 'lucide-react';
import { authenticatedFetch } from './direct-delivery/authenticatedApiHelper';

const customFetch = async (url: string, options: any = {}) => {
  try {
    const result = await authenticatedFetch(url, {
      method: options.method || 'GET',
      headers: options.headers,
      body: options.body
    });
    return {
      status: 200,
      json: async () => result,
      text: async () => typeof result === 'string' ? result : JSON.stringify(result)
    };
  } catch (err: any) {
    const status = err.statusCode || 500;
    return {
      status,
      json: async () => ({ status: 'error', message: err.message, error: err.message }),
      text: async () => err.message || 'Error'
    };
  }
};

const fetch = customFetch;

interface SecurityLog {
  id: string;
  timestamp: string;
  severity: 'INFO' | 'WARNING' | 'CRITICAL' | 'SUCCESS';
  node: string;
  category: string;
  event: string;
  operator: string;
  ip: string;
  details: string;
}

export function NexoraXSecurityTerminal() {
  const [logs, setLogs] = useState<SecurityLog[]>([]);
  const [blockedIPs, setBlockedIPs] = useState<string[]>([]);
  const [blockedSubnets, setBlockedSubnets] = useState<string[]>([]);
  const [geoBlocks, setGeoBlocks] = useState({ enabled: false, countryCodes: [] as string[] });
  const [stats, setStats] = useState({
    totalBlocks: 6,
    totalLogs: 6,
    systemHealth: 'OPTIMAL',
    activeHoneypots: 4,
    coresAvailable: 128
  });

  const [localAuditLogs, setLocalAuditLogs] = useState<any[]>([]);

  const loadLocalAuditLogs = () => {
    const saved = localStorage.getItem('nexorax_audit_logs');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          // Keep only the last 20 access events
          setLocalAuditLogs(parsed.slice(0, 20));
        }
      } catch (e) {
        console.error("Local load fail in audit log parse:", e);
      }
    }
  };

  const [searchTerm, setSearchTerm] = useState('');
  const [severityFilter, setSeverityFilter] = useState<'ALL' | 'INFO' | 'WARNING' | 'CRITICAL' | 'SUCCESS'>('ALL');
  const [nodeFilter, setNodeFilter] = useState<string>('ALL');
  const [isScanning, setIsScanning] = useState(false);
  const [scanStatus, setScanStatus] = useState<string>('');

  // Interactive firewall inputs
  const [newBlockTarget, setNewBlockTarget] = useState(() => {
    return localStorage.getItem('nexorax_security_new_block_target') || '';
  });
  const [newBlockType, setNewBlockType] = useState<'IP' | 'SUBNET'>(() => {
    return (localStorage.getItem('nexorax_security_new_block_type') as any) || 'IP';
  });
  const [isSubmittingBlock, setIsSubmittingBlock] = useState(false);

  // Outbound SMTP Sentry configurations
  const [smtpAdminEmail, setSmtpAdminEmail] = useState(() => {
    return localStorage.getItem('nexorax_security_smtp_admin_email') || 'siddique.mahmood@gmail.com';
  });
  const [smtpTargetIp, setSmtpTargetIp] = useState(() => {
    return localStorage.getItem('nexorax_security_smtp_target_ip') || '192.0.2.1';
  });
  const [smtpCountry, setSmtpCountry] = useState(() => {
    return localStorage.getItem('nexorax_security_smtp_country') || 'GB';
  });
  const [smtpReason, setSmtpReason] = useState(() => {
    return localStorage.getItem('nexorax_security_smtp_reason') || 'Standard outbound security notification check';
  });
  const [smtpLog, setSmtpLog] = useState<string[]>([]);
  const [compiledHtml, setCompiledHtml] = useState('');
  const [isSendingAlert, setIsSendingAlert] = useState(false);
  const [alertSuccessMessage, setAlertSuccessMessage] = useState('');

  useEffect(() => {
    localStorage.setItem('nexorax_security_new_block_target', newBlockTarget);
  }, [newBlockTarget]);

  useEffect(() => {
    localStorage.setItem('nexorax_security_new_block_type', newBlockType);
  }, [newBlockType]);

  useEffect(() => {
    localStorage.setItem('nexorax_security_smtp_admin_email', smtpAdminEmail);
  }, [smtpAdminEmail]);

  useEffect(() => {
    localStorage.setItem('nexorax_security_smtp_target_ip', smtpTargetIp);
  }, [smtpTargetIp]);

  useEffect(() => {
    localStorage.setItem('nexorax_security_smtp_country', smtpCountry);
  }, [smtpCountry]);

  useEffect(() => {
    localStorage.setItem('nexorax_security_smtp_reason', smtpReason);
  }, [smtpReason]);

  const handleSendSmtpAlert = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!smtpAdminEmail || !smtpTargetIp) return;
    setIsSendingAlert(true);
    setSmtpLog(["[INIT] Bootstrapping Outbound Connection Matrix..."]);
    setCompiledHtml('');
    setAlertSuccessMessage('');

    try {
      const response = await fetch('/api/security/send-threat-alert', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          adminEmail: smtpAdminEmail,
          blockedIp: smtpTargetIp,
          country: smtpCountry,
          reason: smtpReason
        })
      });
      const data = await response.json();
      if (data.status === 'success') {
        // Feed the simulated SMTP logs progressively for epic aesthetic realism
        let currentIdx = 0;
        const transcript = data.smtpTranscript || [];
        const intervalId = setInterval(() => {
          if (currentIdx < transcript.length) {
            setSmtpLog(prev => [...prev, transcript[currentIdx]]);
            currentIdx++;
          } else {
            clearInterval(intervalId);
            setCompiledHtml(data.htmlContent);
            setAlertSuccessMessage(`ALERT TRANSMITTED CODE 250: QUEUED OK FOR ${smtpAdminEmail}`);
            setIsSendingAlert(false);
            fetchDefenseStatus(); // Update core security matrix logs automatically
          }
        }, 120);
      } else {
        setSmtpLog(prev => [...prev, `[ERROR] Failed to execute handshakes: ${data.message || 'Unknown protocol deflection'}`]);
        setIsSendingAlert(false);
      }
    } catch (err: any) {
      setSmtpLog(prev => [...prev, `[ERROR] Connection failed: ${err.message || 'SMTP_PORT_465_TIMEOUT'}`]);
      setIsSendingAlert(false);
    }
  };

  // Sync with real Express Perimeter Defense backend
  const fetchDefenseStatus = async () => {
    try {
      const response = await fetch('/api/security/defense-status');
      const data = await response.json();
      if (data.status === 'success') {
        setLogs(data.logs || []);
        setBlockedIPs(data.blockedIPs || []);
        setBlockedSubnets(data.blockedSubnets || []);
        setGeoBlocks(data.geoBlocks || { enabled: false, countryCodes: [] });
        setStats(data.stats || {
          totalBlocks: 0,
          totalLogs: 0,
          systemHealth: 'OPTIMAL',
          activeHoneypots: 4,
          coresAvailable: 128
        });
      }
    } catch (err) {
      console.error("Failed to sync passive perimeter telemetry", err);
    }
  };

  useEffect(() => {
    fetchDefenseStatus();
    loadLocalAuditLogs();
    // Auto refresh telemetry indices logs every 8 seconds
    const interval = setInterval(() => {
      fetchDefenseStatus();
      loadLocalAuditLogs();
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  const handleAddBlock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newBlockTarget.trim()) return;
    setIsSubmittingBlock(true);
    try {
      const response = await fetch('/api/security/add-block', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ blockTarget: newBlockTarget.trim(), type: newBlockType })
      });
      const data = await response.json();
      if (data.status === 'success') {
        setNewBlockTarget('');
        setBlockedIPs(data.blockedIPs);
        setBlockedSubnets(data.blockedSubnets);
        setLogs(data.logs);
      }
    } catch (err) {
      console.error("Failed to append firewall rule", err);
    } finally {
      setIsSubmittingBlock(false);
    }
  };

  const handleReleaseBlock = async (target: string, type: 'IP' | 'SUBNET') => {
    try {
      const response = await fetch('/api/security/release-block', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ blockTarget: target, type })
      });
      const data = await response.json();
      if (data.status === 'success') {
        setBlockedIPs(data.blockedIPs);
        setBlockedSubnets(data.blockedSubnets);
        setLogs(data.logs);
      }
    } catch (err) {
      console.error("Failed to release firewall target block", err);
    }
  };

  const handleToggleGeo = async (enabledState: boolean) => {
    try {
      const response = await fetch('/api/security/toggle-geo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: enabledState })
      });
      const data = await response.json();
      if (data.status === 'success') {
        setGeoBlocks(data.geoBlocks);
        setLogs(data.logs);
      }
    } catch (err) {
      console.error("Failed to toggle GeoIP settings", err);
    }
  };

  const uniqueNodes = useMemo(() => {
    const list = new Set(logs.map(l => l.node));
    return ['ALL', ...Array.from(list)];
  }, [logs]);

  const filteredLogs = useMemo(() => {
    return logs.filter(log => {
      const matchesSearch = 
        log.event.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.details.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.operator.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.category.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesSeverity = severityFilter === 'ALL' || log.severity === severityFilter;
      const matchesNode = nodeFilter === 'ALL' || log.node === nodeFilter;

      return matchesSearch && matchesSeverity && matchesNode;
    });
  }, [logs, searchTerm, severityFilter, nodeFilter]);

  const triggerAuditScan = () => {
    setIsScanning(true);
    setScanStatus("SCANNING SYSTEM DEEP ENDPOINTS...");
    setTimeout(() => {
      setScanStatus("VERIFYING REPLICATED COGNITIVE ARCHITECTURE...");
      setTimeout(() => {
        setScanStatus("SCANNING IP DEVIATIONS AND PORT 3000 SECURITY INCOMING ROUTE...");
        setTimeout(() => {
          fetchDefenseStatus();
          setIsScanning(false);
          setScanStatus("");
        }, 800);
      }, 800);
    }, 800);
  };

  const exportLogsAsJson = () => {
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(logs, null, 2));
    const downloadAnchor = document.createElement('a');
    downloadAnchor.setAttribute("href", dataStr);
    downloadAnchor.setAttribute("download", `nexorax_security_manifest_${new Date().toISOString().slice(0,10)}.json`);
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
  };

  return (
    <div className="w-full max-w-7xl mx-auto p-4 sm:p-6 bg-[#02050c]/90 border border-slate-800 rounded-3xl relative overflow-hidden backdrop-blur-md shadow-[0_0_50px_rgba(6,182,212,0.15)] font-mono">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-cyan-500/5 rounded-full blur-[80px] pointer-events-none translate-x-1/3 -translate-y-1/3"></div>

      {/* Verified Religious Preamble Header (Mandatory) */}
      <div className="bg-slate-950/95 border-b border-cyan-500/30 p-4 mb-6 -mx-4 -mt-4 sm:-mx-6 sm:-mt-6 flex flex-col items-center justify-center text-center gap-2">
        <p className="text-xs sm:text-sm text-emerald-400 font-extrabold tracking-wide leading-relaxed">
          "In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds."
        </p>
        <span className="text-[9px] text-slate-500 uppercase tracking-widest font-black">
          NEXORAX DIGITAL LTD • REGISTERED HQ SC887095 • GLOBAL NEXORAX ARCHITECTURE
        </span>
      </div>

      {/* Terminal Title Area */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center border-b border-slate-800 pb-5 mb-6 gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-cyan-950/40 border border-cyan-500/35 rounded-xl text-cyan-400 shadow-[0_0_15px_rgba(6,182,212,0.15)]">
            <Terminal className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white tracking-widest uppercase flex items-center gap-2">
              COGNITIVE DEFENSE MATRIX &amp; ACTIVE FIREWALL
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse inline-block"></span>
            </h2>
            <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-1">
              Interactive 128 CORES AMD EPYC Perimeter Control Deck &amp; Multi-Zone Subnet Shielder
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={triggerAuditScan}
            disabled={isScanning}
            className="px-4 py-2 bg-cyan-950/45 border border-cyan-500/40 hover:bg-cyan-900/30 text-cyan-400 font-bold uppercase text-[10px] tracking-widest rounded-lg flex items-center gap-2 transition-all cursor-pointer shadow-[0_0_15px_rgba(6,182,212,0.1)] hover:shadow-[0_0_20px_rgba(6,182,212,0.3)] disabled:opacity-50 disabled:cursor-not-allowed animate-pulse"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isScanning ? 'animate-spin' : ''}`} />
            {isScanning ? 'SCANNING SYSTEM...' : 'DEEP SECURITY AUDIT'}
          </button>
          <button
            onClick={exportLogsAsJson}
            className="px-4 py-2 bg-[#02050c] border border-slate-700 hover:border-slate-500 text-slate-300 hover:text-white font-bold uppercase text-[10px] tracking-widest rounded-lg flex items-center gap-2 transition-all cursor-pointer"
          >
            <Download className="w-3.5 h-3.5" />
            EXPORT DIAGNOSTICS
          </button>
        </div>
      </div>

      {isScanning && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mb-6 p-4 bg-cyan-950/20 border border-cyan-500/30 rounded-xl flex items-center gap-3 text-cyan-400 text-xs font-mono uppercase tracking-widest"
        >
          <Activity className="w-4 h-4 text-cyan-400 animate-spin" />
          <span>{scanStatus}</span>
        </motion.div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-[#050914] border border-slate-800/80 p-4 rounded-xl relative overflow-hidden flex flex-col justify-between">
          <div className="absolute top-0 right-0 w-16 h-16 bg-cyan-500/5 blur-[20px] rounded-full"></div>
          <span className="text-[9px] text-slate-500 tracking-widest uppercase font-black">ACTIVE THREAT DEFLECTSTATS</span>
          <span className="text-2xl font-bold text-white block mt-1">{stats.totalBlocks} INTERCEPTIONS</span>
          <span className="text-[8px] text-purple-400 uppercase mt-2">Zero-SLA Lag Blockers</span>
        </div>
        <div className="bg-[#050914] border border-slate-800/80 p-4 rounded-xl relative overflow-hidden flex flex-col justify-between">
          <div className="absolute top-0 right-0 w-16 h-16 bg-emerald-500/5 blur-[20px] rounded-full"></div>
          <span className="text-[9px] text-slate-500 tracking-widest uppercase font-black">SYSTEM HONEYPOTS</span>
          <span className="text-2xl font-bold text-emerald-400 block mt-1">4 ACTIVE DRAGNETS</span>
          <span className="text-[8px] text-emerald-600 uppercase mt-2">Anycast Cloaking Enforced</span>
        </div>
        <div className="bg-[#050914] border border-slate-800/80 p-4 rounded-xl relative overflow-hidden flex flex-col justify-between">
          <div className="absolute top-0 right-0 w-16 h-16 bg-rose-500/5 blur-[20px] rounded-full"></div>
          <span className="text-[9px] text-slate-500 tracking-widest uppercase font-black">PERIMETER QUALITY</span>
          <span className="text-2xl font-bold text-rose-500 block mt-1">ACTIVE {stats.systemHealth}</span>
          <span className="text-[8px] text-rose-600 uppercase mt-2">128 Cores AMD Epyc Cluster</span>
        </div>
        <div className="bg-[#050914] border border-slate-800/80 p-4 rounded-xl relative overflow-hidden flex flex-col justify-between">
          <div className="absolute top-0 right-0 w-16 h-16 bg-violet-500/5 blur-[20px] rounded-full"></div>
          <span className="text-[9px] text-slate-500 tracking-widest uppercase font-black">GEOGRAPHIC PERIMETER GUARD</span>
          <span className="text-2xl font-bold text-violet-400 block mt-1">
            {geoBlocks.enabled ? "STRICT COUNTRY BAN" : "BYPASSED"}
          </span>
          <span className="text-[8px] text-violet-600 uppercase mt-2">Banned Iso: [{geoBlocks.countryCodes.join(", ")}]</span>
        </div>
      </div>

      {/* CORE PERIMETER FIREWALL CONFIGURATION PANEL */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
        
        {/* Left Column: Shield Rule Injection form */}
        <div className="lg:col-span-5 bg-slate-950/80 border border-slate-900 rounded-2xl p-5 flex flex-col justify-between space-y-4">
          <div>
            <div className="flex items-center gap-2 mb-2 text-cyan-400">
              <Shield className="w-4 h-4 text-cyan-400" />
              <span className="text-[10px] font-bold uppercase tracking-widest">FIREWALL RULE INJECTION DECK</span>
            </div>
            <p className="text-[10px] text-slate-400 leading-normal uppercase">
              Inject custom IP addresses or subnet masks directly into the kernel shielder middleware. Local developments are safeguarded natively from lockdowns.
            </p>
          </div>

          <form onSubmit={handleAddBlock} className="space-y-4 pt-1">
            <div className="flex flex-col gap-1 text-left">
              <label className="text-[8.5px] text-slate-500 font-bold uppercase">TARGET IP / CIDR DATA</label>
              <input
                type="text"
                required
                placeholder="e.g. 192.0.2.0/24 or 192.0.2.1"
                value={newBlockTarget}
                onChange={(e) => setNewBlockTarget(e.target.value)}
                className="bg-black/60 border border-slate-800 rounded-lg p-2.5 text-xs text-white placeholder:text-slate-700 outline-none focus:border-cyan-500 tracking-widest font-bold uppercase"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1 text-left">
                <label className="text-[8.5px] text-slate-500 font-bold uppercase">RULE SELECTOR</label>
                <select
                  value={newBlockType}
                  onChange={(e) => setNewBlockType(e.target.value as any)}
                  className="bg-black/60 border border-slate-800 rounded-lg p-2.5 text-xs text-slate-300 font-bold tracking-wider cursor-pointer focus:border-cyan-500 uppercase"
                >
                  <option value="IP">IP ADDRESS</option>
                  <option value="SUBNET">SUBNET (CIDR)</option>
                </select>
              </div>

              <div className="flex items-end">
                <button
                  type="submit"
                  disabled={isSubmittingBlock || !newBlockTarget}
                  className="w-full h-[38px] bg-cyan-950/60 border border-cyan-500/45 hover:bg-cyan-500 text-cyan-300 hover:text-black font-extrabold uppercase text-[10px] tracking-widest rounded-lg flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 transition-all text-center"
                >
                  <Server className="w-3.5 h-3.5" />
                  {isSubmittingBlock ? "APPLYING..." : "COMMIT BLOCK"}
                </button>
              </div>
            </div>
          </form>

          {/* GEOLOCATION CONTROLS DECK */}
          <div className="border-t border-slate-900 pt-4 flex justify-between items-center bg-black/30 p-3 rounded-xl border border-slate-900/60">
            <div className="flex items-center gap-2.5">
              <Globe className="w-4 h-4 text-violet-400" />
              <div className="text-left">
                <span className="text-[9px] text-slate-300 font-bold block uppercase tracking-widest">Active Geo-IP Lockdown</span>
                <span className="text-[8px] text-slate-500 uppercase block font-semibold mt-0.5">Restrict foreign scraper ASN nodes</span>
              </div>
            </div>
            
            <button
              onClick={() => handleToggleGeo(!geoBlocks.enabled)}
              className={`px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest border transition-all cursor-pointer ${
                geoBlocks.enabled 
                  ? 'bg-rose-950/40 border-rose-500 text-rose-400 shadow-[0_0_10px_rgba(239,68,68,0.1)]' 
                  : 'bg-slate-900 border-slate-800 text-slate-500 hover:text-slate-300'
              }`}
            >
              {geoBlocks.enabled ? "SHIELD IN: ON" : "SHIELD IN: OFF"}
            </button>
          </div>
        </div>

        {/* Right Column: Active Firewall Rules Matrix logs list */}
        <div className="lg:col-span-7 bg-slate-950/80 border border-slate-900 rounded-2xl p-5 flex flex-col justify-between">
          <div>
            <div className="flex justify-between items-center mb-4">
              <span className="text-[10px] font-bold text-[#00ff88] uppercase tracking-widest flex items-center gap-2">
                <Lock className="w-3.5 h-3.5 text-[#00ff88]" />
                CURRENT ACTIVE PERIMETER BLOCKS ({blockedIPs.length + blockedSubnets.length})
              </span>
              <span className="text-[8px] bg-slate-900 border border-slate-800/80 px-2 py-0.5 rounded text-slate-400 uppercase tracking-widest font-bold">100% SECURE FOR PORT 3000</span>
            </div>

            <div className="space-y-4 max-h-[290px] overflow-y-auto pr-1">
              <div>
                <span className="text-[8px] text-slate-500 font-bold block mb-1 uppercase tracking-wider">BLOCKADE RANGE SUBNETS</span>
                {blockedSubnets.length === 0 ? (
                  <div className="text-[9px] text-slate-600 uppercase italic p-2 border border-dashed border-slate-900 rounded">No subnets configured for blockades.</div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {blockedSubnets.map((sub) => (
                      <div key={sub} className="flex justify-between items-center bg-[#050914] border border-slate-900 p-2 rounded-lg text-[9px] tracking-widest font-bold text-rose-400">
                        <span>{sub}</span>
                        <button 
                          onClick={() => handleReleaseBlock(sub, 'SUBNET')}
                          className="text-slate-500 hover:text-white hover:bg-rose-950/50 p-1 rounded transition-colors text-[8px] cursor-pointer"
                        >
                          RELEASE
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <span className="text-[8px] text-slate-500 font-bold block mb-1 uppercase tracking-wider">INDIVIDUAL INTERCEPTED BLACKLIST TARGETS</span>
                {blockedIPs.length === 0 ? (
                  <div className="text-[9px] text-slate-600 uppercase italic p-2 border border-dashed border-slate-900 rounded">No specific IP bans enforced.</div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {blockedIPs.map((ip) => (
                      <div key={ip} className="flex justify-between items-center bg-[#050914] border border-slate-900 p-2 rounded-lg text-[9px] tracking-widest font-bold text-rose-400">
                        <span>{ip}</span>
                        <button 
                          onClick={() => handleReleaseBlock(ip, 'IP')}
                          className="text-slate-500 hover:text-white hover:bg-rose-950/50 p-1 rounded transition-colors text-[8px] cursor-pointer"
                        >
                          RELEASE
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1.5 text-[8.5px] text-emerald-400 uppercase font-black tracking-wide border-t border-slate-900 pt-3 mt-4">
            <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
            Active hardware integration active. Node system routing settlements with zero delays
          </div>
        </div>
      </div>

      {/* SMTP OUTBOUND SENTRY TRANSMITTER */}
      <div className="bg-[#030712] border border-slate-900 p-5 rounded-2xl mb-8 shadow-inner text-left">
        <div className="flex items-center gap-2 border-b border-slate-900 pb-3 mb-4">
          <Server className="w-5 h-5 text-rose-500 animate-pulse" />
          <h3 className="text-xs sm:text-sm font-black text-white tracking-[0.2em] uppercase">SMTP OUTBOUND SENTRY TRANSMITTER (PORT 465)</h3>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Sentry Form & Telemetry logs */}
          <div className="space-y-4">
            <p className="text-[10px] text-slate-400 uppercase leading-relaxed">
              Compile and trigger a high-impact Glassmorphic perimeter deflection alert to inform corporate administrators of active lockdowns. This triggers the exact Python logic natively in our Node.js SMTP engine proxy.
            </p>

            <form onSubmit={handleSendSmtpAlert} className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="flex flex-col gap-1 text-left">
                  <label className="text-[8.5px] text-slate-500 font-bold uppercase">ADMIN EMAIL RECIPIENT</label>
                  <input
                    type="email"
                    required
                    value={smtpAdminEmail}
                    onChange={(e) => setSmtpAdminEmail(e.target.value)}
                    className="bg-black/60 border border-slate-800 rounded-lg p-2.5 text-xs text-white placeholder:text-slate-700 outline-none focus:border-rose-500 tracking-wider font-bold"
                  />
                </div>

                <div className="flex flex-col gap-1 text-left">
                  <div className="flex justify-between items-center">
                    <label className="text-[8.5px] text-slate-500 font-bold uppercase">TARGET IP ADDR</label>
                    {blockedIPs.length > 0 && (
                      <span 
                        onClick={() => setSmtpTargetIp(blockedIPs[0])}
                        style={{ cursor: 'pointer' }}
                        className="text-[8px] text-cyan-400 uppercase underline hover:text-cyan-300"
                      >
                        Auto-fill active block
                      </span>
                    )}
                  </div>
                  <input
                    type="text"
                    required
                    value={smtpTargetIp}
                    onChange={(e) => setSmtpTargetIp(e.target.value)}
                    className="bg-black/60 border border-slate-800 rounded-lg p-2.5 text-xs text-white placeholder:text-slate-700 outline-none focus:border-rose-500 tracking-widest font-bold font-mono"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="flex flex-col gap-1 text-left sm:col-span-1">
                  <label className="text-[8.5px] text-slate-500 font-bold uppercase">ORIGIN COUNTRY</label>
                  <input
                    type="text"
                    required
                    value={smtpCountry}
                    onChange={(e) => setSmtpCountry(e.target.value)}
                    className="bg-black/60 border border-slate-800 rounded-lg p-2.5 text-xs text-white placeholder:text-slate-700 outline-none focus:border-rose-500 tracking-widest font-bold uppercase font-sans"
                  />
                </div>

                <div className="flex flex-col gap-1 text-left sm:col-span-2">
                  <label className="text-[8.5px] text-slate-500 font-bold uppercase">DEFLECTION REASON</label>
                  <input
                    type="text"
                    required
                    value={smtpReason}
                    onChange={(e) => setSmtpReason(e.target.value)}
                    className="bg-black/60 border border-slate-800 rounded-lg p-2.5 text-xs text-white placeholder:text-slate-700 outline-none focus:border-rose-500 tracking-wide font-bold"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isSendingAlert}
                style={{ cursor: isSendingAlert ? 'not-allowed' : 'pointer' }}
                className="w-full py-2.5 bg-rose-950/40 border border-rose-500 hover:bg-rose-500 text-rose-400 hover:text-black font-extrabold uppercase text-[10px] tracking-[0.2em] rounded-lg transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
              >
                <Server className="w-3.5 h-3.5" />
                {isSendingAlert ? "HANDSHAKING ENCRYPTION DECK..." : "FORGE & EXECUTE SMTP TRANSMISSION"}
              </button>
            </form>

            {alertSuccessMessage && (
              <div className="p-3 bg-emerald-950/30 border border-emerald-500 text-emerald-400 text-[10px] font-bold uppercase tracking-wider rounded-lg">
                ✓ {alertSuccessMessage}
              </div>
            )}

            {/* SMTP Network logs terminal screen */}
            <div className="space-y-1 text-left">
              <span className="text-[8.5px] text-slate-500 font-bold uppercase tracking-widest">LIVE SMTP OUTBOUND CONSOLE TRANSCRIPT</span>
              <div className="bg-black p-3 rounded-lg border border-slate-900 font-mono text-[9px] text-slate-400 h-[170px] overflow-y-auto space-y-1 select-text scrollbar-thin scrollbar-thumb-slate-800">
                {smtpLog.length === 0 ? (
                  <span className="text-slate-600 block italic">// Outbound queue empty. Prepare configuration inputs and commit transmit session.</span>
                ) : (
                  smtpLog.map((logLine, idx) => {
                    let color = 'text-slate-400';
                    if (logLine.startsWith('[SMTP_REPLY]')) color = 'text-cyan-400';
                    else if (logLine.startsWith('[SMTP_CMD]')) color = 'text-purple-400';
                    else if (logLine.startsWith('[SMTP_SUCCESS]')) color = 'text-emerald-400 font-bold';
                    else if (logLine.startsWith('[ERROR]')) color = 'text-rose-400 font-bold';
                    return (
                      <div key={idx} className={`${color} break-all hover:bg-white/5 px-2 py-0.5 rounded text-left`}>
                        {logLine}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* Sentry Live CSS HTML Preview */}
          <div className="flex flex-col h-full justify-between text-left">
            <span className="text-[8.5px] text-slate-500 font-bold uppercase tracking-widest mb-1 select-none">GLASSMORPHIC EMAIL BODY PREVIEW RENDER</span>
            <div className="flex-1 border border-slate-900 rounded-lg overflow-hidden bg-[#02050e] h-[370px] flex flex-col justify-between relative min-h-[300px]">
              {compiledHtml ? (
                <iframe
                  title="Glassmorphic Threat Alert Render Frame"
                  srcDoc={compiledHtml}
                  className="w-full h-full border-0 bg-transparent"
                  sandbox="allow-same-origin"
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-full p-6 text-center space-y-3">
                  <div className="w-10 h-10 rounded-full border border-dashed border-slate-800 flex items-center justify-center text-slate-700 animate-spin font-black">
                    *
                  </div>
                  <span className="text-[10px] text-slate-500 uppercase tracking-widest font-black">Waiting for first compilation trigger.</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Filter Matrix */}
      <div className="bg-[#02050c] border border-slate-800/60 p-4 rounded-2xl mb-6 flex flex-col lg:flex-row gap-4 items-center">
        {/* Search Input */}
        <div className="relative w-full lg:flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="FILTER BY LOG STRING, OPERATOR, CATEGORY OR IP ADDRESS..."
            className="w-full bg-black/40 border border-slate-800 rounded-lg py-2 pl-9 pr-4 text-xs text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-cyan-500"
          />
        </div>

        {/* Severity selection */}
        <div className="flex gap-2 w-full lg:w-auto overflow-x-auto">
          {(['ALL', 'SUCCESS', 'INFO', 'WARNING', 'CRITICAL'] as const).map((sev) => (
            <button
              key={sev}
              type="button"
              onClick={() => setSeverityFilter(sev)}
              className={`text-[9px] font-bold px-3 py-1.5 rounded-md border tracking-widest transition-all cursor-pointer whitespace-nowrap ${
                severityFilter === sev
                  ? sev === 'CRITICAL'
                    ? 'bg-rose-950/40 border-rose-500 text-rose-400'
                    : sev === 'WARNING'
                    ? 'bg-amber-950/40 border-amber-500 text-amber-400'
                    : sev === 'SUCCESS'
                    ? 'bg-emerald-950/40 border-emerald-500 text-emerald-400'
                    : 'bg-cyan-950/40 border-cyan-500 text-cyan-400'
                  : 'bg-black/30 border-slate-800 text-slate-500 hover:text-slate-300'
              }`}
            >
              {sev}
            </button>
          ))}
        </div>

        {/* Node Filter */}
        <div className="flex items-center gap-2 w-full lg:w-auto">
          <span className="text-[9px] text-slate-500 font-bold uppercase tracking-widest whitespace-nowrap">Node Filter:</span>
          <select
            value={nodeFilter}
            onChange={(e) => setNodeFilter(e.target.value)}
            className="bg-black/80 border border-slate-800 rounded-lg text-[9px] text-slate-300 p-1.5 focus:outline-none focus:border-cyan-500 font-mono font-bold tracking-wider uppercase cursor-pointer"
          >
            {uniqueNodes.map(node => (
              <option key={node} value={node}>{node}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Terminal Output */}
      <div className="bg-black/95 border border-slate-800 rounded-2xl overflow-hidden flex flex-col">
        {/* Terminal Titlebar */}
        <div className="bg-slate-900 border-b border-slate-800 p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-rose-500 inline-block"></span>
            <span className="w-3 h-3 rounded-full bg-amber-500 inline-block"></span>
            <span className="w-3 h-3 rounded-full bg-emerald-500 inline-block"></span>
            <span className="text-[10px] text-slate-400 font-bold ml-2">SYSTEM SHIELD CONTROL LINE // FLOOD SENTRY DIARY</span>
          </div>
          <div className="text-[8.5px] text-slate-600 font-bold">
            SECURE TERMINAL // PORT 3000 STRICT
          </div>
        </div>

        {/* Interactive read-only logs table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left font-mono border-collapse">
            <thead>
              <tr className="border-b border-slate-800 bg-slate-950/50 text-slate-500 text-[9px] tracking-widest font-black uppercase">
                <th className="p-4">timestamp</th>
                <th className="p-4">id</th>
                <th className="p-4">severity</th>
                <th className="p-4">logical node</th>
                <th className="p-4">event category</th>
                <th className="p-4">trigger source / operator</th>
                <th className="p-4">source ip</th>
                <th className="p-4 py-3 min-w-[320px]">operational payload description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-900 text-[10px]">
              {filteredLogs.length > 0 ? (
                filteredLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-slate-900/30 transition-colors">
                    <td className="p-4 text-slate-500 whitespace-nowrap">
                      {new Date(log.timestamp).toLocaleTimeString()}<br/>
                      <span className="text-[8px] opacity-60 font-sans">{new Date(log.timestamp).toLocaleDateString()}</span>
                    </td>
                    <td className="p-4 font-bold text-slate-300 whitespace-nowrap">{log.id}</td>
                    <td className="p-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[8.5px] font-black uppercase tracking-widest ${
                        log.severity === 'CRITICAL' 
                          ? 'bg-rose-950/50 text-rose-400 border border-rose-800/30'
                          : log.severity === 'WARNING'
                          ? 'bg-amber-950/50 text-amber-400 border border-amber-800/30'
                          : log.severity === 'SUCCESS'
                          ? 'bg-emerald-950/50 text-emerald-400 border border-emerald-800/30'
                          : 'bg-cyan-950/50 text-cyan-400 border border-cyan-800/30'
                      }`}>
                        {log.severity === 'CRITICAL' && <AlertCircle className="w-2.5 h-2.5" />}
                        {log.severity === 'WARNING' && <AlertTriangle className="w-2.5 h-2.5" />}
                        {log.severity === 'SUCCESS' && <CheckCircle className="w-2.5 h-2.5" />}
                        {log.severity === 'INFO' && <Eye className="w-2.5 h-2.5" />}
                        {log.severity}
                      </span>
                    </td>
                    <td className="p-4 font-bold text-slate-400 whitespace-nowrap">{log.node}</td>
                    <td className="p-4 text-slate-300 font-bold whitespace-nowrap">{log.category}</td>
                    <td className="p-4 text-slate-300 whitespace-nowrap">{log.operator}</td>
                    <td className="p-4 text-slate-400 font-bold whitespace-nowrap">{log.ip}</td>
                    <td className="p-4 text-slate-200 tracking-wide break-words max-w-sm font-sans">{log.details}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="p-12 text-center text-slate-500 uppercase tracking-widest font-black">
                    -- NO SECURE ACCESS RECORDS CORRESPONDING TO THIS FILTER CRITERIA EXIST --
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- RECONSTRUCTED LOCAL STORAGE SECURITY AUDIT LOG SYSTEM (LAST 20 EVENTS) --- */}
      <div className="bg-[#050914]/90 border border-purple-500/30 rounded-2xl p-5 mb-6 relative overflow-hidden backdrop-blur-md shadow-[0_0_30px_rgba(168,85,247,0.15)] mt-8">
        <div className="absolute top-0 right-0 w-64 h-32 bg-purple-500/5 blur-[40px] rounded-full pointer-events-none"></div>
        
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-slate-800 pb-3 mb-4 gap-3">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-purple-950/40 border border-purple-500/30 rounded-lg text-purple-400 shadow-[0_0_10px_rgba(168,85,247,0.15)]">
              <Database className="w-5 h-5 text-purple-400 animate-pulse" />
            </div>
            <div className="text-left">
              <h3 className="text-xs sm:text-xs font-black text-rose-300 tracking-[0.25em] uppercase flex items-center gap-1.5">
                LOCAL SECURITY AUDIT LOGS
                <span className="text-[8px] bg-purple-900/30 text-purple-400 border border-purple-500/25 px-1.5 py-0.5 rounded font-bold">LOCALSTORAGE ENGINE</span>
              </h3>
              <p className="text-[9px] text-slate-500 uppercase mt-0.5">Visualization of the last 20 host & core local system access events</p>
            </div>
          </div>
          <div className="flex items-center gap-2 font-mono self-stretch sm:self-auto justify-between sm:justify-start">
            <button
              onClick={loadLocalAuditLogs}
              style={{ cursor: 'pointer' }}
              className="px-2.5 py-1 text-[9.5px] bg-[#02050c] border border-purple-500/30 rounded hover:bg-purple-950/30 hover:border-purple-400 text-purple-400 transition cursor-pointer font-bold uppercase tracking-wider"
            >
              Refresh Audit Feed
            </button>
            <span className="text-[9px] px-2 py-1 rounded bg-[#02050c] text-zinc-400 border border-slate-800/80 uppercase font-black tracking-widest whitespace-nowrap">
              TRACK: {localAuditLogs.length} / 20 EVENTS
            </span>
          </div>
        </div>

        {/* Dynamic Metric Breakdown for Audit Event types */}
        {localAuditLogs.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4 font-mono text-[9px]">
            <div className="p-2.5 rounded-xl bg-emerald-950/20 border border-emerald-500/20 flex justify-between items-center">
              <span className="text-emerald-400 uppercase font-bold tracking-wider flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-400" /> Secured Settlements / Actions</span>
              <span className="text-white bg-emerald-950 border border-emerald-400/30 px-2 py-0.5 rounded font-black text-[10px]">{localAuditLogs.filter(l => l.status === 'SECURED' || l.status === 'SUCCESS' || l.status === 'success' || l.status === 'SUCCESSFUL').length}</span>
            </div>
            <div className="p-2.5 rounded-xl bg-amber-950/20 border border-amber-500/20 flex justify-between items-center">
              <span className="text-amber-400 uppercase font-bold tracking-wider flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-amber-450" /> Alerts &amp; Active Routing</span>
              <span className="text-white bg-amber-950 border border-amber-400/30 px-2 py-0.5 rounded font-black text-[10px]">{localAuditLogs.filter(l => l.status === 'ACTIVE_ROUTING' || l.status === 'WARNING' || l.status === 'warning' || l.status === 'ACTIVE_ROUTING').length}</span>
            </div>
            <div className="p-2.5 rounded-xl bg-rose-950/20 border border-rose-500/20 flex justify-between items-center">
              <span className="text-rose-400 uppercase font-bold tracking-wider flex items-center gap-1"><AlertCircle className="w-3 h-3 text-rose-400" /> Deflected / Blocked Attempt</span>
              <span className="text-white bg-rose-950 border border-rose-400/30 px-2 py-0.5 rounded font-black text-[10px]">{localAuditLogs.filter(l => l.status === 'BLOCKED' || l.status === 'DENIED' || l.status === 'denied').length}</span>
            </div>
          </div>
        )}

        {/* Scrollable Event Feed Cards */}
        <div className="space-y-3 max-h-[360px] overflow-y-auto pr-1">
          {localAuditLogs.length === 0 ? (
            <div className="text-center py-10 bg-[#02050c]/50 border border-dashed border-slate-800 rounded-xl text-slate-550 text-[11px] italic uppercase tracking-wider font-bold">
              -- No local security audit events found in terminal cache memory --
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {localAuditLogs.map((log) => {
                const isSecured = log.status === 'SECURED' || log.status === 'SUCCESS' || log.status === 'success' || log.status === 'SUCCESSFUL';
                const isWarning = log.status === 'ACTIVE_ROUTING' || log.status === 'WARNING' || log.status === 'warning' || log.status === 'ACTIVE_ROUTING';
                const isBlocked = log.status === 'BLOCKED' || log.status === 'DENIED' || log.status === 'denied';

                return (
                  <motion.div
                    key={log.id || `local-log-${log.timestamp}-${Date.now()}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-3 bg-slate-950/75 border border-slate-900 rounded-xl space-y-2 flex flex-col justify-between transition-all hover:border-purple-500/30 hover:bg-[#060a18]/45 relative group"
                  >
                    <div className="absolute top-0 left-0 w-1.5 h-full rounded-l-md pointer-events-none transition-all" style={{
                      backgroundColor: isSecured ? '#10b981' : isWarning ? '#f59e0b' : '#f43f5e'
                    }} />
                    
                    <div className="flex justify-between items-start gap-2 pl-2">
                      <div className="space-y-0.5 text-left">
                        <span className="text-[8px] text-slate-500 font-bold block bg-[#02050c] px-1.5 py-0.5 rounded border border-slate-900 inline-block">
                          {new Date(log.timestamp).toLocaleDateString()} @ {new Date(log.timestamp).toLocaleTimeString()}
                        </span>
                        <p className="text-slate-100 font-sans font-bold text-xs uppercase mt-1 leading-tight tracking-tight">{log.action}</p>
                      </div>
                      <span className={`px-2 py-0.5 rounded text-[8.5px] font-black uppercase tracking-widest border ${
                        isSecured ? 'bg-emerald-950/40 border-emerald-500/20 text-emerald-400' :
                        isWarning ? 'bg-amber-950/40 border-amber-500/25 text-amber-400' :
                        'bg-rose-950/40 border-rose-500/20 text-rose-400'
                      }`}>
                        {log.status === 'SUCCESSFUL' ? 'SECURED' : log.status}
                      </span>
                    </div>
                    
                    <div className="text-[10px] space-y-1 font-mono text-zinc-350 bg-[#02050c] border border-slate-900/60 p-2 rounded-lg text-left pl-3.5">
                      <div className="text-[8.5px] font-mono text-zinc-550 uppercase">Logical Module: <span className="text-cyan-400 font-bold">{log.module}</span></div>
                      <div className="font-sans leading-relaxed text-zinc-300 break-words mt-1">{log.details}</div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Corporate Compliance Notice */}
      <div className="mt-6 border-t border-slate-800/80 pt-4 flex flex-col md:flex-row justify-between items-center text-[9px] text-slate-500 uppercase tracking-widest gap-4 font-bold text-center">
        <span>GLOBAL REVENUE ENGINE MAPPED TO Secure Payment Provider OK</span>
        <span>08000 622827 SUPPORT PIPELINE DEPLOYED</span>
        <span>REPLICATION BUFFER TO DRIVE SYNC OK</span>
      </div>
    </div>
  );
}
